import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../config';
import { getBondChatSocket } from '../../app/redux/bondchat/generalChatSingleton';
import { shouldSkipCachedFeed } from '../../utils/feedFreshness';
import { isNetworkOffline } from '../../utils/requireNetwork';

export const fetchShowcaseP2PBets = createAsyncThunk(
  'p2pShowcase/fetchShowcaseP2PBets',
  async ({ page = 1, limit = 9 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/p2p?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch showcase P2P bets');
      }

      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  },
  {
    condition: ({ page = 1, limit = 9, force = false, append = false } = {}, { getState }) => {
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().p2pShowcase;
      return !shouldSkipCachedFeed({
        force,
        append,
        fetchedAt: state.lastFetchedAt,
        hasData: state.lastFetchedAt != null,
        page,
        limit,
        cachedPage: state.lastFetchPage,
        cachedLimit: state.lastFetchLimit,
      });
    },
  }
);

/** Load one P2P by id for share / deep links (`?w=`). */
export const fetchShowcaseP2PBetById = createAsyncThunk(
  'p2pShowcase/fetchShowcaseP2PBetById',
  async (p2pId, { rejectWithValue }) => {
    const id = String(p2pId || '').trim();
    if (!id) return rejectWithValue('P2P id is required');
    try {
      const response = await fetch(`${API_BASE_URL}/api/p2p/${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch P2P bet');
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  },
  {
    condition: (p2pId, { getState }) => {
      if (isNetworkOffline()) return false;
      const id = String(p2pId || '').trim();
      if (!id) return false;
      const bets = getState().p2pShowcase.p2pBets || [];
      return !bets.some((b) => String(b._id) === id);
    },
  }
);

const initialState = {
  p2pBets: [],
  isLoading: false,
  isLoadingMore: false,
  isLoadingFocus: false,
  focusError: null,
  error: null,
  lastFetchedAt: null,
  lastFetchPage: 1,
  lastFetchLimit: 9,
  pagination: {
    page: 1,
    limit: 9,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  }
};

const p2pShowcaseSlice = createSlice({
  name: 'p2pShowcase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateP2PBet: (state, action) => {
      const updated = action.payload;
      if (!updated?._id) return;
      const updatedId = String(updated._id);
      const idx = state.p2pBets.findIndex((b) => String(b._id) === updatedId);
      if (idx !== -1) state.p2pBets[idx] = updated;
    },
    removeP2PBet: (state, action) => {
      const id = action.payload;
      state.p2pBets = state.p2pBets.filter((b) => b._id !== id);
      if (state.pagination.totalCount > 0) {
        state.pagination.totalCount -= 1;
      }
    },
    addP2PBet: (state, action) => {
      const exists = state.p2pBets.some((b) => b._id === action.payload._id);
      if (!exists) {
        state.p2pBets.unshift(action.payload);
        state.pagination.totalCount += 1;
      }
    },
    clearFocusError: (state) => {
      state.focusError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShowcaseP2PBets.pending, (state, action) => {
        const append = action.meta?.arg?.append;
        if (append) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchShowcaseP2PBets.fulfilled, (state, action) => {
        const page = action.meta?.arg?.page || 1;
        const limit = action.meta?.arg?.limit ?? state.lastFetchLimit;
        const append = action.meta?.arg?.append;
        state.isLoading = false;
        state.isLoadingMore = false;
        const incoming = action.payload.data || [];
        if (append && page > 1) {
          const ids = new Set(state.p2pBets.map((b) => b._id));
          state.p2pBets = [
            ...state.p2pBets,
            ...incoming.filter((b) => !ids.has(b._id)),
          ];
        } else {
          const preserveIds = new Set(
            (action.meta?.arg?.preserveIds || []).map((id) => String(id))
          );
          const kept =
            preserveIds.size === 0
              ? []
              : state.p2pBets.filter(
                  (b) =>
                    preserveIds.has(String(b._id)) &&
                    !incoming.some((row) => String(row._id) === String(b._id))
                );
          state.p2pBets = kept.length ? [...kept, ...incoming] : incoming;
        }
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
        if (page === 1 && !append) {
          state.lastFetchedAt = Date.now();
          state.lastFetchPage = page;
          state.lastFetchLimit = limit;
        }
      })
      .addCase(fetchShowcaseP2PBets.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload;
        if (!action.meta?.arg?.append && state.p2pBets.length === 0) {
          state.p2pBets = [];
        }
      })
      .addCase(fetchShowcaseP2PBetById.pending, (state) => {
        state.isLoadingFocus = true;
        state.focusError = null;
      })
      .addCase(fetchShowcaseP2PBetById.fulfilled, (state, action) => {
        state.isLoadingFocus = false;
        state.focusError = null;
        const bet = action.payload;
        if (!bet?._id) return;
        const id = String(bet._id);
        const idx = state.p2pBets.findIndex((b) => String(b._id) === id);
        if (idx !== -1) {
          state.p2pBets[idx] = bet;
        } else {
          state.p2pBets.unshift(bet);
        }
      })
      .addCase(fetchShowcaseP2PBetById.rejected, (state, action) => {
        state.isLoadingFocus = false;
        if (action.payload) state.focusError = action.payload;
      });
  },
});

export const { clearError, updateP2PBet, removeP2PBet, addP2PBet, clearFocusError } =
  p2pShowcaseSlice.actions;

export const selectP2PShowcase = (state) => state.p2pShowcase;
export const selectShowcaseP2PBets = (state) => state.p2pShowcase.p2pBets;
export const selectP2PShowcaseLoading = (state) => state.p2pShowcase.isLoading;
export const selectP2PShowcaseLoadingMore = (state) => state.p2pShowcase.isLoadingMore;
export const selectP2PShowcaseLoadingFocus = (state) => state.p2pShowcase.isLoadingFocus;
export const selectP2PShowcaseFocusError = (state) => state.p2pShowcase.focusError;
export const selectP2PShowcaseError = (state) => state.p2pShowcase.error;
export const selectP2PShowcasePagination = (state) => state.p2pShowcase.pagination;

let p2pListenersAttached = false;

/** Attach real-time P2P listeners to the shared BondChat socket. */
export const initializeP2PShowcaseListeners = (dispatch) => {
  const socket = getBondChatSocket();
  if (!socket || p2pListenersAttached) return;
  p2pListenersAttached = true;

  socket.on("newP2PBetCreated", (bet) => {
    if (bet?._id) dispatch(addP2PBet(bet));
  });

  socket.on("p2pBetJoined", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pBetJoinedSuccess", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pBetCancelled", ({ p2pId }) => {
    if (p2pId) dispatch(removeP2PBet(p2pId));
  });

  socket.on("p2pBetCancelledSuccess", ({ p2pId }) => {
    if (p2pId) dispatch(removeP2PBet(p2pId));
  });

  socket.on("p2pBetEdited", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pBetEditedSuccess", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pInviteAccepted", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pInviteAcceptedSuccess", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pInviteRejected", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });

  socket.on("p2pInviteRejectedSuccess", ({ p2pBet }) => {
    if (p2pBet?._id) dispatch(updateP2PBet(p2pBet));
  });
};

export default p2pShowcaseSlice.reducer;
